import evVideo from './EvVideo'

/**
 * Reviews modal controller
 * - Opens modal on [data-video-open]
 * - Embeds iframe into [data-video-container]
 * - Closes on [data-video-close], overlay, or Esc
 */
class ReviewsModal {
    constructor(root) {
        this.root = root
        this.modal = root.querySelector('[data-video-modal]')
        this.container = root.querySelector('[data-video-container]')
        this.overlay = root.querySelector('.header-overlay[data-video-close]')
        this.closeBtn = root.querySelector('.reviews__modal-close[data-video-close]')

        this.rutubeMessageHandler = null
        this.currentIframe = null

        this.handleOpen = this.handleOpen.bind(this)
        this.handleClose = this.handleClose.bind(this)
        this.handleKeydown = this.handleKeydown.bind(this)
    }

    withAutoplay(url, type) {
        if (!url) {
            return ''
        }

        try {
            const parsedUrl = new URL(url)

            if (type === 'dzen') {
                if (!parsedUrl.searchParams.has('autoplay')) {
                    parsedUrl.searchParams.set('autoplay', '1')
                }
            }

            if (type === 'rutube') {
                if (!parsedUrl.searchParams.has('autoplay')) {
                    parsedUrl.searchParams.set('autoplay', 'true')
                }
            }

            if (type === 'youtube') {
                if (!parsedUrl.searchParams.has('autoplay')) {
                    parsedUrl.searchParams.set('autoplay', '1')
                }
                if (!parsedUrl.searchParams.has('playsinline')) {
                    parsedUrl.searchParams.set('playsinline', '1')
                }
            }

            if (type === 'vk') {
                if (!parsedUrl.searchParams.has('autoplay')) {
                    parsedUrl.searchParams.set('autoplay', '1')
                }
            }

            if (type === 'optimacros') {
                if (!parsedUrl.searchParams.has('autoplay')) {
                    parsedUrl.searchParams.set('autoplay', '1')
                }
                if (!parsedUrl.searchParams.has('warningTitle')) {
                    parsedUrl.searchParams.set('warningTitle', '0')
                }
                if (!parsedUrl.searchParams.has('controlBar')) {
                    parsedUrl.searchParams.set('controlBar', '0')
                }
                if (!parsedUrl.searchParams.has('peertubeLink')) {
                    parsedUrl.searchParams.set('peertubeLink', '0')
                }
            }

            return parsedUrl.toString()
        } catch (e) {
            if (type === 'dzen' || type === 'youtube' || type === 'vk' || type === 'optimacros') {
                return url.includes('?') ? `${url}&autoplay=1` : `${url}?autoplay=1`
            }

            if (type === 'rutube') {
                return url.includes('?') ? `${url}&autoplay=true` : `${url}?autoplay=true`
            }

            return url
        }
    }

    getTypeFromUrl(url) {
        if (!url) {
            return ''
        }

        if (url.includes('rutube.ru/')) {
            return 'rutube'
        }

        if (url.includes('dzen.ru/')) {
            return 'dzen'
        }

        if (url.includes('youtube.com/') || url.includes('youtu.be/')) {
            return 'youtube'
        }

        if (url.includes('vk.com/') || url.includes('vkvideo.ru/')) {
            return 'vk'
        }

        if (url.includes('video.optimacros.com/')) {
            return 'optimacros'
        }

        return ''
    }

    getOptimacrosEmbedUrl(rawValue) {
        if (!rawValue) {
            return ''
        }

        let normalizedValue = rawValue.trim()
        const iframeMatch = normalizedValue.match(/src=["']([^"']+)["']/i)

        if (iframeMatch?.[1]) {
            normalizedValue = iframeMatch[1]
        }

        try {
            const parsedUrl = new URL(normalizedValue)

            if (parsedUrl.hostname !== 'video.optimacros.com') {
                return ''
            }

            const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

            if (pathParts[0] === 'videos' && pathParts[1] === 'embed' && pathParts[2]) {
                return this.withAutoplay(parsedUrl.toString(), 'optimacros')
            }

            if (pathParts[0] === 'w' && pathParts[1]) {
                return this.withAutoplay(
                    `https://video.optimacros.com/videos/embed/${encodeURIComponent(pathParts[1])}`,
                    'optimacros',
                )
            }

            return ''
        } catch (e) {
            if (!/^[A-Za-z0-9]+$/.test(normalizedValue)) {
                return ''
            }

            return this.withAutoplay(
                `https://video.optimacros.com/videos/embed/${encodeURIComponent(normalizedValue)}`,
                'optimacros',
            )
        }
    }

    buildVideoElement(src, type) {
        if (type === 'wp') {
            const video = document.createElement('video')
            const source = document.createElement('source')

            video.controls = true
            video.autoplay = true
            video.playsInline = true
            video.setAttribute('playsinline', '')
            video.setAttribute('preload', 'metadata')
            video.title = 'Video player'

            source.src = src
            video.appendChild(source)

            return video
        }

        const iframe = document.createElement('iframe')
        iframe.className = type === 'dzen' ? 'dzen-iframe' : ''
        iframe.width = '100%'
        iframe.height = '480'
        iframe.frameBorder = '0'
        iframe.scrolling = 'no'
        iframe.allowFullscreen = true
        iframe.setAttribute('allowfullscreen', '')
        iframe.src = src

        if (type) {
            iframe.title = `Video player: ${type}`
        } else {
            iframe.title = 'Video player'
        }

        iframe.setAttribute(
            'allow',
            'clipboard-write; autoplay; fullscreen; accelerometer; gyroscope; picture-in-picture; encrypted-media',
        )

        if (type === 'optimacros') {
            iframe.setAttribute(
                'sandbox',
                'allow-same-origin allow-scripts allow-popups allow-forms',
            )
        }

        return iframe
    }

    init() {
        if (!this.modal || !this.container) {
            return
        }

        this.root.addEventListener('click', (e) => {
            const trigger = e.target.closest('[data-video-open]')
            if (trigger) {
                e.preventDefault()
                this.handleOpen(trigger)
            }
        })
        ;[this.overlay, this.closeBtn].forEach((el) => {
            if (el) {
                el.addEventListener('click', this.handleClose)
            }
        })
    }

    handleOpen(trigger) {
        const src = trigger.getAttribute('data-video-src') || ''
        const videoFile = trigger.getAttribute('data-video-file') || ''
        const type = trigger.getAttribute('data-video-type') || ''
        const vid = trigger.getAttribute('data-video-id') || ''

        let embedUrl = src || ''
        if (!embedUrl && type === 'dzen' && vid && evVideo?.getDzenEmbedUrl) {
            embedUrl = evVideo.getDzenEmbedUrl(vid)
        }

        if (!embedUrl && type === 'rutube' && vid) {
            embedUrl = `https://rutube.ru/play/embed/${encodeURIComponent(vid)}/`
        }

        if (!embedUrl && type === 'optimacros' && vid) {
            embedUrl = this.getOptimacrosEmbedUrl(vid)
        }

        if (type === 'wp' && videoFile) {
            embedUrl = videoFile
        }

        const effectiveType = type || this.getTypeFromUrl(embedUrl)

        if (effectiveType !== 'wp') {
            embedUrl = this.withAutoplay(embedUrl, effectiveType)
        }

        if (!embedUrl) {
            return
        }

        const player = this.buildVideoElement(embedUrl, effectiveType)

        this.container.innerHTML = ''
        this.container.appendChild(player)

        this.currentIframe = effectiveType === 'wp' ? null : player

        if (effectiveType === 'rutube') {
            this.attachRutubeAutoplay(player)
        }

        this.modal.hidden = false
        if (this.overlay) {
            this.overlay.classList.add('header-overlay--is-active')
        }
        document.addEventListener('keydown', this.handleKeydown)
    }

    handleClose() {
        this.modal.hidden = true
        this.container.innerHTML = ''
        this.detachRutubeAutoplay()
        this.currentIframe = null
        if (this.overlay) {
            this.overlay.classList.remove('header-overlay--is-active')
        }
        document.removeEventListener('keydown', this.handleKeydown)
    }

    attachRutubeAutoplay(iframe) {
        this.detachRutubeAutoplay()

        this.rutubeMessageHandler = (event) => {
            if (event.origin !== 'https://rutube.ru') {
                return
            }

            if (!iframe?.contentWindow || event.source !== iframe.contentWindow) {
                return
            }

            let message
            try {
                message = JSON.parse(event.data)
            } catch (e) {
                return
            }

            if (!message || message.type !== 'player:ready') {
                return
            }

            try {
                iframe.contentWindow.postMessage(
                    JSON.stringify({
                        type: 'player:play',
                        data: {},
                    }),
                    '*',
                )
            } catch (e) {
                // noop
            }
        }

        window.addEventListener('message', this.rutubeMessageHandler)
    }

    detachRutubeAutoplay() {
        if (!this.rutubeMessageHandler) {
            return
        }

        window.removeEventListener('message', this.rutubeMessageHandler)
        this.rutubeMessageHandler = null
    }

    handleKeydown(e) {
        if (e.key === 'Escape') {
            this.handleClose()
        }
    }
}

export default function initReviewsModal() {
    document.querySelectorAll('.reviews').forEach((root) => {
        const modal = new ReviewsModal(root)
        modal.init()
    })
}
